import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import { Form } from 'react-final-form';
import { map } from 'lodash';
import { useSnackbar } from 'notistack';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import {
  validateCombinedAddressLength,
  validateMaxLength,
  validateMinLength,
  validatePhoneNumber,
  validateValueWithRegex,
  validateZipCode,
} from 'common/forms/formValidations';
import { useDispatch } from 'react-redux';
import { addExternalTransferPharmacy } from 'store/thunks/pharmacyThunks';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import { formatZipCode } from 'common/utils';

const AddExternalTransferPharmacyButton = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const handleAddExternalTransferPharmacy = (formData) => {
    setLoading(true);
    const input = {
      pharmacyName: formData.pharmacyName,
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2 || null,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      phoneNumber: formData.phoneNumber,
      fax: formData.fax,
      npi: formData.npi,
      dea: formData.dea || null,
      pharmacistDetails: {
        name: formData.pharmacistName,
        npi: formData.pharmacistNpi,
      },
    };

    dispatch(addExternalTransferPharmacy(input))
      .then((response) => {
        if (response) {
          enqueueSnackbar('External pharmacy added successfully', { variant: 'success' });
          setShowModal(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Button
        sx={{ height: 33, mr: 2 }}
        variant='contained'
        color='primary'
        onClick={handleOpenModal}
      >
        Add New
      </Button>

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='ExternalTransferPharmacy-AddButton-header'
        fullWidth
        maxWidth='md'
      >
        <DialogTitle id='ExternalTransferPharmacy-AddButton-header'>
          Add External Pharmacy
        </DialogTitle>

        <Form
          onSubmit={handleAddExternalTransferPharmacy}
          render={({ handleSubmit, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <Typography variant='h6' sx={{ mb: 2, mt: 1, fontWeight: 600 }}>
                  Pharmacy Information
                </Typography>

                <TextField
                  id='ExternalTransferPharmacy-AddButton-pharmacyName-input'
                  label='Pharmacy Name'
                  name='pharmacyName'
                  required
                  autoFocus
                  validations={[
                    validateMinLength('Pharmacy Name must be at least 3 characters', 3),
                    validateMaxLength('Maximum 50 characters', 50),
                  ]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-addressLine1-input'
                  label='Address Line 1'
                  name='addressLine1'
                  required
                  validations={[
                    validateMinLength('Address Line 1 must be at least 3 characters', 3),
                    validateCombinedAddressLength(),
                  ]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-addressLine2-input'
                  label='Address Line 2'
                  name='addressLine2'
                  validations={[validateCombinedAddressLength()]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-city-input'
                  label='City'
                  name='city'
                  required
                  validations={[validateMinLength('City must be at least 3 characters', 3)]}
                />

                <SelectField
                  id='ExternalTransferPharmacy-AddButton-state-dropdown'
                  label='State'
                  name='state'
                  required
                >
                  {map(usStateAbbreviation, ({ name, abbreviation }) => (
                    <MenuItem value={abbreviation} key={abbreviation}>
                      {name}
                    </MenuItem>
                  ))}
                </SelectField>

                <TextField
                  id='ExternalTransferPharmacy-AddButton-zip-input'
                  label='ZIP Code'
                  name='zip'
                  type='zip'
                  required
                  validations={[validateZipCode()]}
                  format={formatZipCode}
                  maxLength={10}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-phoneNumber-input'
                  label='Phone Number'
                  name='phoneNumber'
                  required
                  validations={[validatePhoneNumber()]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-fax-input'
                  label='Fax Number'
                  name='fax'
                  required
                  validations={[validatePhoneNumber('Invalid Fax Number')]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-npi-input'
                  label='Pharmacy NPI'
                  name='npi'
                  required
                  validations={[validateValueWithRegex('Must be 10 digits', /^\d{10}$/)]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-dea-input'
                  label='DEA Number'
                  name='dea'
                />

                <Typography variant='h6' sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                  Pharmacist Information
                </Typography>

                <TextField
                  id='ExternalTransferPharmacy-AddButton-pharmacistName-input'
                  label='Pharmacist Name'
                  name='pharmacistName'
                  required
                  validations={[
                    validateMinLength('Pharmacist Name must be at least 3 characters', 3),
                  ]}
                />

                <TextField
                  id='ExternalTransferPharmacy-AddButton-pharmacistNpi-input'
                  label='Pharmacist NPI'
                  name='pharmacistNpi'
                  required
                  validations={[validateValueWithRegex('Must be 10 digits', /^\d{10}$/)]}
                />
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  disabled={invalid}
                  type='submit'
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

export default AddExternalTransferPharmacyButton;
